import api, { ApiError } from './apiService';
import { derivePrimaryTitleHsl } from './colorUtils';

/**
 * Base API URL from environment configuration
 */
const API_URL = import.meta.env.VITE_API_BASE_URL;

/**
 * Get the API URL for assets
 * @returns The base URL for API asset requests
 */
const getAssetBaseUrl = (): string => {
	return API_URL || '';
};

/**
 * Icon asset configuration for favicons and app icons
 */
interface IconAsset {
	rel: string;
	type?: string;
	sizes?: string;
	href: string;
}

/**
 * Theme configuration with CSS custom properties
 */
interface ThemeConfig {
	primary?: string;
	'bg-body-alt'?: string;
	'headings-font-weight'?: string;
	[key: string]: string | undefined;
}

/**
 * Application configuration from API
 */
interface AppConfig {
	handle: string;
	nicename: string;
	theme?: ThemeConfig;
	[key: string]: unknown;
}

/**
 * RGB color values
 */
interface RGB {
	r: number;
	g: number;
	b: number;
}

/**
 * Configuration service for multi-tenant app configuration
 */
const fnlConfig = {
	/** Current app identifier */
	fnlId: null as string | null,
	/** Base path for app assets */
	basePath: null as string | null,
	/** Current app configuration */
	currentConfig: null as AppConfig | null,
	/** Icon assets for favicons and app icons */
	iconAssets: [] as IconAsset[],

	/**
	 * Initializes configuration service with app config
	 * @param initialHref - The initial URL/href for configuration
	 * @returns Promise resolving to config service or null if failed
	 */
	init: async (initialHref: string) => {
		fnlConfig.currentConfig = await fnlConfig.fetchConfigFromAPI(initialHref);
		if (fnlConfig.currentConfig) {
			fnlConfig.fnlId = fnlConfig.currentConfig.handle;
			fnlConfig.basePath = `${getAssetBaseUrl()}/assets/${fnlConfig.fnlId}/favicons`;
			fnlConfig.iconAssets = fnlConfig.createIconAssets();
			fnlConfig.handleDomChanges(fnlConfig.fnlId);
			return fnlConfig;
		}
		return null;
	},

	/**
	 * Handles DOM changes for app configuration
	 * @param id - The app identifier
	 */
	handleDomChanges: (id: string) => {
		fnlConfig.setSkinToDom();
		fnlConfig.setHeadElements(id);
		fnlConfig.setThemeVariables(id);
	},

	/**
	 * Gets configuration for specific app ID
	 * @param id - The app identifier
	 * @returns App configuration or null if not found
	 */
	getConfig: (id: string): AppConfig | null => {
		if (fnlConfig.currentConfig && fnlConfig.currentConfig.handle === id) {
			return fnlConfig.currentConfig;
		}
		return null;
	},

	/**
	 * Gets current app configuration
	 * @returns Current app configuration or null
	 */
	getCurrentConfig: (): AppConfig | null => {
		return fnlConfig.currentConfig;
	},

	/**
	 * Fetches configuration from API
	 * @param initialHref - The initial URL/href for configuration
	 * @param retryWithoutAuth - Internal flag to prevent infinite loops
	 * @returns Promise resolving to app configuration
	 * @throws Error if API call fails or password protection is required
	 */
	fetchConfigFromAPI: async (initialHref: string, retryWithoutAuth = false): Promise<AppConfig> => {
		try {
			const config = await api.config.post(initialHref);
			return config as AppConfig;
		} catch (error) {
			if (error instanceof ApiError && error.code === 'password_required' && !retryWithoutAuth) {
				const passwordError = new Error('PASSWORD_REQUIRED');
				(passwordError as Error & { originalError: Error }).originalError = error;
				throw passwordError;
			}
			console.error('Failed to fetch config from API:', error);
			throw error;
		}
	},

	/**
	 * Initializes the app after successful authentication
	 * Updates internal state and applies config to DOM
	 * @param configData - The configuration data received after authentication
	 * @returns The initialized fnlConfig object ready for the app
	 * @throws Error if configuration is invalid or missing required handle
	 */
	initWithAuthenticatedConfig: (configData: unknown) => {
		// Validate and cast the config data
		const config = configData as { handle?: string; nicename?: string; [key: string]: unknown };
		const handle = config.handle;

		if (!handle) {
			throw new Error('Invalid configuration: missing handle');
		}

		// Now we know handle exists, create properly typed AppConfig
		const appConfig: AppConfig = {
			handle,
			nicename: config.nicename || handle,
			...config
		};

		// Update the config service's internal state
		fnlConfig.currentConfig = appConfig;
		fnlConfig.fnlId = handle;
		fnlConfig.basePath = `${getAssetBaseUrl()}/assets/${handle}/favicons`;
		fnlConfig.iconAssets = fnlConfig.createIconAssets();

		// Apply config to DOM
		fnlConfig.handleDomChanges(handle);

		// Return the fnlConfig object for the App
		return {
			currentConfig: appConfig,
			fnlId: handle,
			basePath: fnlConfig.basePath,
			iconAssets: fnlConfig.iconAssets
		};
	},

	/**
	 * Extracts label information from hostname
	 * @param hostname - The hostname to extract label from (defaults to window.location.hostname)
	 * @returns Object containing label slug and display name
	 */
	extractLabelFromHostname: (hostname: string = window.location.hostname) => {
		const labelSlug = hostname.split('.')[0]; // Extract label from subdomain
		const labelName = labelSlug.charAt(0).toUpperCase() + labelSlug.slice(1);
		return { labelSlug, labelName };
	},

	/**
	 * Sets app skin identifier to DOM
	 */
	setSkinToDom: () => {
		if (fnlConfig.fnlId) {
			document.body.setAttribute('data-fnlid', fnlConfig.fnlId);
		}
	},

	/**
	 * Creates icon assets configuration for favicons and app icons
	 * @returns Array of icon asset configurations
	 */
	createIconAssets: (): IconAsset[] => {
		const iconAssets: IconAsset[] = [
			{ rel: 'icon', type: 'image/x-icon', href: `${fnlConfig.basePath}/favicon.ico` },
			{ rel: 'shortcut icon', href: `${fnlConfig.basePath}/favicon.ico` },
			{
				rel: 'apple-touch-icon',
				sizes: '180x180',
				href: `${fnlConfig.basePath}/apple-touch-icon.png`
			},
			{
				rel: 'apple-touch-icon-precomposed',
				sizes: '57x57',
				href: `${fnlConfig.basePath}/apple-touch-icon-57x57.png`
			},
			{
				rel: 'apple-touch-icon-precomposed',
				sizes: '72x72',
				href: `${fnlConfig.basePath}/apple-touch-icon-72x72.png`
			},
			{
				rel: 'apple-touch-icon-precomposed',
				sizes: '114x114',
				href: `${fnlConfig.basePath}/apple-touch-icon-114x114.png`
			},
			{
				rel: 'apple-touch-icon-precomposed',
				sizes: '120x120',
				href: `${fnlConfig.basePath}/apple-touch-icon-120x120.png`
			},
			{
				rel: 'apple-touch-icon-precomposed',
				sizes: '152x152',
				href: `${fnlConfig.basePath}/apple-touch-icon-152x152.png`
			},
			{
				rel: 'icon',
				sizes: '16x16',
				type: 'image/png',
				href: `${fnlConfig.basePath}/favicon-16x16.png`
			},
			{
				rel: 'icon',
				sizes: '32x32',
				type: 'image/png',
				href: `${fnlConfig.basePath}/favicon-32x32.png`
			}
		];
		return iconAssets;
	},

	/**
	 * Sets head elements for app configuration (icons, title, meta tags)
	 * @param id - The app identifier
	 */
	setHeadElements(id: string) {
		// Update or create all icon assets
		fnlConfig.iconAssets.forEach((asset) => {
			const selector = Object.entries(asset)
				.map(([key, value]) => `[${key}="${value}"]`)
				.join('');

			let element = document.querySelector(`link${selector}`) as HTMLLinkElement | null;

			if (element) {
				// Update existing element
				Object.entries(asset).forEach(([key, value]) => {
					element?.setAttribute(key, value);
				});
			} else {
				// Create new element
				element = document.createElement('link');
				Object.entries(asset).forEach(([key, value]) => {
					element?.setAttribute(key, value);
				});
				document.head.appendChild(element);
			}
		});

		// Update title
		const title = document.querySelector('title');
		if (title) {
			const config = fnlConfig.getConfig(id);
			if (config) {
				title.textContent = config.nicename;
			}
		}

		// Update Microsoft tile
		const msTileColor = document.querySelector(
			"meta[name='msapplication-TileColor']"
		) as HTMLMetaElement | null;
		if (msTileColor) {
			msTileColor.content = '#FFFFFF';
		}

		const msTileImage = document.querySelector(
			"meta[name='msapplication-TileImage']"
		) as HTMLMetaElement | null;
		if (msTileImage) {
			msTileImage.content = `${fnlConfig.basePath}/mstile-114x114.png`;
		}
	},

	/**
	 * Converts hex color to RGB values
	 * @param hex - Hex color string (with or without #)
	 * @returns RGB object or null if invalid hex
	 */
	hexToRgb: (hex: string): RGB | null => {
		const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
		return result
			? {
					r: Number.parseInt(result[1], 16),
					g: Number.parseInt(result[2], 16),
					b: Number.parseInt(result[3], 16)
				}
			: null;
	},

	/**
	 * Darkens a hex color by percentage
	 * @param hex - Hex color string
	 * @param percent - Percentage to darken (0-100)
	 * @returns Darkened hex color or original if invalid
	 */
	darkenColor: (hex: string, percent: number): string => {
		const rgb = fnlConfig.hexToRgb(hex);
		if (!rgb) return hex;

		const factor = (100 - percent) / 100;
		const r = Math.round(rgb.r * factor);
		const g = Math.round(rgb.g * factor);
		const b = Math.round(rgb.b * factor);

		return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
	},

	/**
	 * Lightens a hex color by percentage
	 * @param hex - Hex color string
	 * @param percent - Percentage to lighten (0-100)
	 * @returns Lightened hex color or original if invalid
	 */
	lightenColor: (hex: string, percent: number): string => {
		const rgb = fnlConfig.hexToRgb(hex);
		if (!rgb) return hex;

		const factor = percent / 100;
		const r = Math.round(rgb.r + (255 - rgb.r) * factor);
		const g = Math.round(rgb.g + (255 - rgb.g) * factor);
		const b = Math.round(rgb.b + (255 - rgb.b) * factor);

		return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
	},

	/**
	 * Converts hex color to HSL format for Tailwind
	 * @param hex - Hex color string
	 * @returns HSL string (e.g., "210 72% 45%") or null if invalid
	 */
	hexToHsl: (hex: string): string | null => {
		const rgb = fnlConfig.hexToRgb(hex);
		if (!rgb) return null;

		const r = rgb.r / 255;
		const g = rgb.g / 255;
		const b = rgb.b / 255;

		const max = Math.max(r, g, b);
		const min = Math.min(r, g, b);
		let h = 0;
		let s = 0;
		const l = (max + min) / 2;

		if (max !== min) {
			const d = max - min;
			s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

			switch (max) {
				case r:
					h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
					break;
				case g:
					h = ((b - r) / d + 2) / 6;
					break;
				case b:
					h = ((r - g) / d + 4) / 6;
					break;
			}
		}

		return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
	},

	/**
	 * Sets CSS custom properties for theme configuration
	 * @param id - The app identifier
	 */
	setThemeVariables: (id: string) => {
		const config = fnlConfig.getConfig(id);
		if (config?.theme) {
			const { primary, 'bg-body-alt': bodyAlt } = config.theme;

			// Set Tailwind primary color
			if (primary) {
				const hsl = fnlConfig.hexToHsl(primary);

				if (hsl) {
					// Set Tailwind primary color
					document.documentElement.style.setProperty('--color-primary', `hsl(${hsl})`);

					document.documentElement.style.setProperty(
						'--color-primary-title',
						`hsl(${derivePrimaryTitleHsl(primary) ?? '0 0% 12%'})`
					);
					document.documentElement.style.setProperty(
						'--color-primary-step-border',
						`hsl(${derivePrimaryTitleHsl(primary, 6) ?? '0 0% 10%'})`
					);

					// Set foreground color (white text on primary background)
					document.documentElement.style.setProperty(
						'--color-primary-foreground',
						'hsl(0 0% 100%)'
					);
				}
			}

			// Set CSS custom properties for theme compatibility
			// Note: Legacy property name maintained for backward compatibility
			if (bodyAlt) {
				document.documentElement.style.setProperty('--body-bg-alt', bodyAlt);
			}

			// Set logo URL based on handle (from backend API)
			document.documentElement.style.setProperty(
				'--logo-url',
				`url('${getAssetBaseUrl()}/assets/${config.handle}/logo.svg')`
			);

			// Set any additional theme variables from config
			Object.entries(config.theme).forEach(([property, value]) => {
				if (
					!['primary', 'body', 'bg-body-alt', 'headings-font-weight'].includes(property) &&
					value
				) {
					document.documentElement.style.setProperty(property, value);
				}
			});
		}
	}
};

export default fnlConfig;
